<template>
  <div
    class="min-h-hero flex flex-col pt-shh lg:pt-0 pb-15 bg-paint px-ogs-large"
  >
    <transition name="fade">
      <ColourSelector
        v-if="showColourSelector"
        :is-paint-product="true"
        :default-colour="selectedColour"
        :palettes="palettes"
        :swatch-book="swatchBook"
        :colour-styles="colourStyles"
        @select-colour="handleSelectColour"
      />
    </transition>
    <div class="flex-1 flex flex-col justify-center items-center lg:py-15">
      <div class="flex flex-col items-center pt-36 pb-16 lg:pt-0">
        <label class="text-xxs text-theme font-semibold uppercase mb-1"
          >Selected colour</label
        >
        <div class="relative">
          <transition name="fade">
            <div
              v-if="isLoading"
              role="alert"
              data-testid="loading-message"
              class="absolute inset-0 flex items-center justify-center z-20"
            >
              <LoaderSpinner class="w-10 h-10 text-theme" />
            </div>
          </transition>
          <div
            :class="{ 'opacity-25': isLoading, 'opacity-100': !isLoading }"
            class="relative transition-opacity duration-300 z-10"
          >
            <button
              class="link flex items-center border-b border-theme py-2 space-x-2"
              @click="showColourSelector = true"
            >
              <div class="text-xl text-theme">{{ selectedColour.title }}</div>
              <div class="caret bg-theme" aria-hidden="true"></div>
            </button>
          </div>
        </div>
        <div
          v-if="selectedColour.accent || selectedColour.ourFaves"
          class="flex justify-center items-center space-x-3 mt-5"
        >
          <div v-if="selectedColour.accent" class="flex items-center">
            <div class="h-2 text-theme">
              <LabelAccent />
            </div>
            <ButtonInfo
              class="ml-1"
              themed
              heading="Accent Colours"
              body="Our accent range is our dark, bold and bright colours.  It comes in a hard wearing low sheen finish and is available in our wall paint, enamel paint and exterior paint only."
            />
          </div>
          <div v-if="selectedColour.ourFaves" class="h-2 text-theme">
            <LabelOurFaves />
          </div>
        </div>
      </div>
      <div class="border border-theme rounded-xl w-full max-w-xl mx-px">
        <div class="border-b border-theme px-10 sm:px-15 pt-6 pb-4">
          <div class="px-5 flex justify-center">
            <SelectMenuWithLabel
              v-if="paintTypes.length > 1"
              id="paint-type-select"
              class="mb-4 text-theme"
              :value="product.slug"
              label="Paint Type"
              themed
              :info-heading="'Paint Type'"
              :info-text="'Different uses call for different paints. Pick your paint below'"
              select-style="alternative"
              :options="paintTypeOptions"
              @input="changePaintType($event)"
            >
            </SelectMenuWithLabel>
          </div>
          <p
            v-if="product.description"
            class="text-theme text-sm md:text-base text-center"
          >
            {{ product.description }}
          </p>
        </div>
        <transition name="fade" mode="out-in">
          <form
            v-if="!isLoading"
            id="variantForm"
            name="variantForm"
            @submit="handleSubmit"
          >
            <ProductVariantSelector
              class="flex justify-center divide-x divide-theme h-24 lg:h-15"
              :sheens="variantSheens"
              :sizes="variantSizes"
              :selected-sheen="selectedSheen"
              :selected-size="selectedSize"
              selector-style="alternative"
              :info-heading="'Sheen'"
              :info-text="'Different people prefer different finishes. Choose your preferred sheen'"
              @change-sheen="selectedSheen = $event"
              @change-size="selectedSize = $event"
            />
          </form>
          <div
            v-else
            class="flex justify-center divide-x divide-theme h-24 lg:h-15"
          >
            <div class="flex-1 flex items-center justify-center">
              <LoaderSpinner class="w-10 h-10 text-theme" />
            </div>
            <div class="flex-1 flex items-center justify-center">
              <LoaderSpinner class="w-10 h-10 text-theme" />
            </div>
          </div>
        </transition>
        <div
          class="flex flex-col sm:flex-row items-center border-t border-theme py-5 sm:py-3 px-10 gap-y-2 gap-x-8"
        >
          <button
            class="flex-1 w-full sm:w-auto btn-pill min-h-10"
            :class="{ 'btn-pill--alt': selectedColour.textColour === 'white' }"
            @click="$store.commit('openPaintCalculator')"
          >
            Size up my space
          </button>
          <ProductAddToCartForm
            class="flex-1 btn-pill--strong"
            :class="{
              'btn-pill--strong-alt': selectedColour.textColour === 'white',
            }"
            form="variantForm"
            :message-reversed="true"
            button-style="pill"
            :variant="variantSelected"
            :loading="isLoading"
          />
        </div>
      </div>
      <div class="gap-x-3 flex items-center lg:hidden mt-6">
        <SiteFavouritesAddButton
          :selected-colour="selectedColour"
        ></SiteFavouritesAddButton>
        <ButtonFullScreenColour :story="selectedColour.text" />
      </div>
      <div v-if="testPotProductId" class="lg:hidden mt-6 flex justify-center">
        <TestPotAddToCart
          class="min-h-10"
          button-style="alt"
          :loading="isLoading"
          :colour-title="selectedColour.title"
          :test-pot="testPot"
          :sku="variantSelected ? variantSelected.sku : ''"
          :product-title="product.title"
          :sheen="selectedSheen.title"
          :paint-colour="selectedColour.hex"
          :text-colour="selectedColour.textColour"
          source="paint_product_page"
          >{{ testPotButtonText }}</TestPotAddToCart
        >
      </div>
    </div>
    <div class="grid grid-cols-12 items-end gap-x-gs gap-y-4">
      <div class="hidden lg:col-span-5 lg:flex items-center gap-x-5">
        <div v-if="testPotProductId">
          <TestPotAddToCart
            class="min-h-10"
            button-style="alt"
            :loading="isLoading"
            :colour-title="selectedColour.title"
            :test-pot="testPot"
            :sku="variantSelected ? variantSelected.sku : ''"
            :product-title="product.title"
            :sheen="selectedSheen.title"
            :paint-colour="selectedColour.hex"
            :text-colour="selectedColour.textColour"
            source="paint_product_page"
            >{{ testPotButtonText }}</TestPotAddToCart
          >
        </div>
        <div class="gap-x-3 flex items-center lg:col-span-2">
          <SiteFavouritesAddButton
            :selected-colour="selectedColour"
          ></SiteFavouritesAddButton>
          <ButtonFullScreenColour :story="selectedColour.text" />
        </div>
      </div>
      <div
        class="col-span-12 lg:col-span-2 pt-20 lg:pt-0 flex justify-center items-center"
      >
        <button
          class="link flex flex-col justify-center items-center focus:outline-none text-theme"
          @click="handleClick"
        >
          <span class="text-xl">Explore</span>
          <span class="w-4 h-6 mt-3">
            <IconArrowDown />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../js/apiClient";
import ColourSelector from "../ColourSelector/ColourSelector.vue";
import ProductAddToCartForm from "./ProductAddToCartForm.vue";
import ProductMixin from "./ProductMixin";
import ProductVariantSelector from "./ProductVariantSelector.vue";
import TestPotAddToCart from "./TestPotAddToCart.vue";
import ButtonInfo from "../ui/ButtonInfo.vue";
import LoaderSpinner from "../ui/LoaderSpinner.vue";
import SelectMenuWithLabel from "../ui/SelectMenuWithLabel.vue";
import LabelAccent from "../../../templates/_includes/svg/label-accent-alt.svg";
import LabelOurFaves from "../../../templates/_includes/svg/label-our-faves.svg";
import ButtonFullScreenColour from "../ui/ButtonFullScreenColour.vue";
import SiteFavouritesAddButton from "../SiteFavourites/SiteFavouritesAddButton.vue";
import IconArrowDown from "../../../templates/_includes/svg/icon-arrow-down.svg";

export default {
  name: "ProductPaint",

  components: {
    ColourSelector,
    ProductAddToCartForm,
    ProductVariantSelector,
    ButtonInfo,
    LoaderSpinner,
    SelectMenuWithLabel,
    LabelAccent,
    LabelOurFaves,
    ButtonFullScreenColour,
    TestPotAddToCart,
    SiteFavouritesAddButton,
    IconArrowDown,
  },

  mixins: [ProductMixin],

  props: {
    swatchBook: {
      type: [Object, Boolean],
      required: true,
    },
    paintTypes: {
      type: [Array, Boolean],
      required: true,
    },
    baseUrl: {
      type: String,
      required: true,
    },
    colourStyles: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      showColourSelector: false,
      variantDetails: [],
    };
  },

  computed: {
    paintTypeOptions() {
      return this.paintTypes.map(({ slug, title }) => ({
        value: slug,
        label: title,
      }));
    },

    variantSheens() {
      const sheens = this.variantDetails.map((detail) => {
        return {
          id: parseInt(detail.id),
          title: detail.title,
          slug: detail.slug,
        };
      });

      return sheens || [];
    },

    variantSizes() {
      const sheen = this.variantDetails.filter((sheen) => {
        return sheen.id === this.selectedSheen.id;
      });

      // JANK ALERT: Ordering the sizes on title
      return (
        sheen[0]?.sizes.sort(function (a, b) {
          if (parseInt(a.title) < parseInt(b.title)) {
            return -1;
          }
          if (parseInt(b.title) < parseInt(a.title)) {
            return 1;
          }
          return 0;
        }) || []
      );
    },

    variantSelected() {
      if (!this.variantDetails.length) return {};
      const selectedSheen = this.variantDetails.filter(
        (variant) => variant.title === this.selectedSheen.title
      );
      const selectedSize = selectedSheen[0]?.sizes.filter(
        (size) => size.title == this.selectedSize.title
      );
      return selectedSize ? selectedSize[0].variant : {};
    },

    testPotButtonText() {
      return `Get a Test Pot - $${this.testPot ? this.testPot.salePrice : 6}`;
    },
  },

  watch: {
    showColourSelector(value) {
      this.$store.commit("toggleSelector", value);
      if (value === true) {
        document.documentElement.classList.add("no-scroll");
        document.body.classList.add("no-scroll");
      } else {
        document.documentElement.classList.remove("no-scroll");
        document.body.classList.remove("no-scroll");
      }
    },
  },

  beforeMount() {
    const { hex, textColour } = this.selectedColour;
    this.$store.commit("setSiteTheme", [hex, textColour]);
    this.$store.commit("setPaintTheme", [hex, textColour]);
  },

  methods: {
    /**
     * Handles the validation of the size select when adding a product to cart
     * Shows HTML 5 validation if no size has benn chosen
     * @author Daniel Siemers <daniel@batch.nz>
     * @param e Form submit event
     * @return void
     */
    handleSubmit(e) {
      e.preventDefault();
      e.target.reportValidity();
    },

    handleSelectColour(colour) {
      this.selectedColour = colour;
      this.showColourSelector = false;
    },

    handleClick() {
      const target =
        document
          .getElementsByClassName("js-scroll-target")[0]
          .getBoundingClientRect().top + window.pageYOffset;

      window.scrollTo({
        top: target,
        behavior: "smooth",
      });
    },

    async getQueryIds(queryBody) {
      this.isLoading = true;
      let ids = null;

      try {
        const requestOptions = {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(queryBody),
        };

        ids = await fetch(
          "/actions/url-query-handler/paint-query-string",
          requestOptions
        )
          .then((response) => response.json())
          .then((result) => result)
          .catch((error) => console.error("error", error));
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
      }

      this.isLoading = false;
      return ids;
    },

    /**
     * Gets the variants for the product with the selected colour and resets the
     * selected size
     * @author Daniel Siemers <daniel@batch.nz>
     * @return void
     */
    async getData() {
      this.isLoading = true;

      // Make api call for paint variants
      try {
        const { variants } = await api.variants.queryPaintVariants(
          this.product.id,
          this.selectedColour.id
        );

        // Formats the data into a tree structure
        this.formatData(variants);
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
        this.variantDetails = null;
      }

      this.resetSize();

      // Make api call for paint test pots
      try {
        const testPot = await api.variants.queryPaintTestPots(
          this.testPotProductId,
          this.selectedColour.id
        );

        this.testPot = testPot.variants[0];

        this.isLoading = false;
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
        this.variantDetails = null;
        this.isLoading = false;
      }
    },

    /**
     * Formats the data from the query into a tree structure with sheen
     * name at the highest level, then size, then variant
     * @author Daniel Siemers <daniel@batch.nz>
     * @param variants Variants from the query
     * @return void
     */
    formatData(variants) {
      const details = [];

      variants.map((variant, index) => {
        // Resets the selected sheen
        if (index === 0) {
          this.selectedSheen = {
            id: parseInt(variant.paintSheen[0].id),
            title: variant.paintSheen[0].title,
            slug: variant.paintSheen[0].slug,
          };
        }
        for (const sheen of details) {
          // Add the size and variant to the sheen if it is already present in the array
          if (sheen.title === variant.paintSheen[0].title) {
            sheen.sizes.push({
              id: parseInt(variant.size[0].id),
              title: variant.size[0].title,
              slug: variant.size[0].slug,
              variant,
            });

            return;
          }
        }

        // Add the sheen size and variant if the sheen is not already present in the array
        details.push({
          id: parseInt(variant.paintSheen[0].id),
          title: variant.paintSheen[0].title,
          slug: variant.paintSheen[0].slug,
          sizes: [
            {
              id: parseInt(variant.size[0].id),
              title: variant.size[0].title,
              slug: variant.size[0].slug,
              variant,
            },
          ],
        });
      });

      this.variantDetails = details;
    },

    changeSheen() {
      this.resetSize();
    },

    /**
     * Resets the size selected
     * @author Daniel Siemers <daniel@batch.nz>
     * @return void
     */
    resetSize() {
      if (
        !this.variantSizes.some(
          (variantSize) => variantSize.id === this.selectedSize.id
        )
      ) {
        this.selectedSize = this.variantSizes[0];
      }
    },

    productUrl(productType) {
      return `${this.baseUrl}paint/${productType}?colour=${this.selectedColour.slug}&sheen=low-sheen&size=1-litre`;
    },

    changePaintType(productType) {
      window.location.href = this.productUrl(productType);
    },
  },
};
</script>
